<template>
    <div class="thank-you-messages">
        <div class="vx-row justify-start mb-4">
            <div class="vx-col pb-1 sm:w-1/2 w-full">
                <label class="mb-1 block">المجموعة</label>
                <multiselect v-model="selectedGroup" placeholder="اختر مجموعة" selectLabel="اضف هذه المجموعة"
                    deselectLabel="احذف هذه المجموعة" :options="volunteerGroups" label="name" track-by="id"
                    :loading="isLoadingGroups">
                </multiselect>
            </div>
        </div>
        <ag-table :data-source-api="getAllThankYouMessages" :grid-options="gridOptions" :permissions="permissions"
        :rtl="false" :is-entities-response="false">
        </ag-table>
        <tm-popup title="تفاصيل رسالة الشكر" ref="thankYouMessagePopup">
            <div class="vx-row justify-start">
                <div class="vx-col sm:w-1/2 w-full mb-2 mt-2">
                    <tm-input class="w-full" name="اسم المشرف" placeholder="اسم المشرف" type="text" v-model="thankYouMessage.supervisorName" readonly="true"/>
                </div>
                <div class="vx-col sm:w-1/2 w-full mb-2 mt-2">
                    <tm-input class="w-full" name="اسم المتطوع" placeholder="اسم المتطوع" type="text" v-model="thankYouMessage.volunteerName" readonly="true"/>
                </div>
                <div class="vx-col sm:w-1/2 w-full mb-2 mt-2">
                    <tm-input class="w-full" name="تاريخ الإرسال" placeholder="تاريخ الإرسال" type="text" v-model="thankYouMessage.createdAt" readonly="true"/>
                </div>
                <div class="vx-col w-full mb-2 mt-4">
                    <p class="mt-2 px-2" style="font-size: smaller">الرسالة</p>
                    <tm-text-area class="w-full" name="الرسالة" v-model="thankYouMessage.message" readonly="true"/>
                </div>
            </div>
        </tm-popup>
    </div>
</template>

<script>
import utilities from "@/app/shared/utilities/index";
import TmInput from "@/app/shared/shared-components/TmInput";
import TmPopup from "@/app/shared/shared-components/TmPopup";
import TmTextArea from "@/app/shared/shared-components/TmTextArea";
import AgTableBtnCell from "../../shared/shared-components/ag-grid/AgTableBtnCell";
import AgTable from '../../shared/shared-components/ag-grid/AgTableServerSide.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { RepositoryFactory } from '../volunteer-repositories/volunteerRepositoryFactory';

const thankYouMessagesRepo = RepositoryFactory.get('thankYouMessagesRepository');
const supervisorsRepo = RepositoryFactory.get('supervisorsRepository');

// One message fans out to every supervisor of the volunteer's group, so the
// supervisors arrive as a `recipients` array rather than a single relation.
function resolveSupervisorName(data) {
    if (!data) return "-";
    if (data.recipients && data.recipients.length) {
        let names = [];
        for (let i = 0; i < data.recipients.length; i++) {
            let recipient = data.recipients[i];
            if (recipient && recipient.supervisor && recipient.supervisor.name) {
                names.push(recipient.supervisor.name);
            }
        }
        if (names.length) return names.join("، ");
    }
    return "-";
}

function resolveVolunteerName(data) {
    if (!data) return "-";
    if (data.volunteer && data.volunteer.name) return data.volunteer.name;
    return "-";
}

// The API selects the volunteer relation as `{ id, name }` only — no phone
// number is returned yet. Kept so the column fills in once the backend joins it.
function resolvePhoneNumber(data) {
    if (!data) return "-";
    if (data.volunteerPhoneNumber) return data.volunteerPhoneNumber;
    if (data.volunteer && data.volunteer.phoneNumber) return data.volunteer.phoneNumber;
    return "-";
}

function resolveCreatedAt(data) {
    if (!data || !data.createdAt) return "-";
    return utilities.formatDate(data.createdAt, "yyyy-MM-DD HH:mm");
}

export default {
    name: "ThankYouMessages",
    components: {
        AgTable,
        // eslint-disable-next-line vue/no-unused-components
        AgTableBtnCell,
        TmPopup,
        TmInput,
        TmTextArea,
        Multiselect,
    },
    data() {
        return {
        gridApi: null,
        gridOptions: null,
        permissions: {},
        volunteerGroups: [],
        isLoadingGroups: false,
        selectedGroup: null,
        thankYouMessage: {
            supervisorName: null,
            volunteerName: null,
            phoneNumber: null,
            createdAt: null,
            message: null,
        },
        };
    },
    watch: {
        selectedGroup() {
            this.gridApi.refreshServerSideStore({ purge: true });
        },
    },
    methods: {
        createColDefs() {
        let self = this;
        // Only تاريخ الإرسال carries a `filter:`/`sortable:`. The identity
        // columns read from joined relations, which the list endpoint does not
        // declare as filterable.
        return [
            {
            headerName: 'اسم المشرف',
            sortable: false,
            width: 210,
            valueGetter: function getObject(params) {
                return resolveSupervisorName(params.data);
            },
            },
            {
            headerName: 'اسم المتطوع',
            sortable: false,
            width: 210,
            valueGetter: function getObject(params) {
                return resolveVolunteerName(params.data);
            },
            },
            {
            headerName: 'الرسالة',
            field: 'message',
            sortable: false,
            width: 400,
            valueGetter: function getObject(params) {
                if (!params.data || !params.data.message) return "-";
                return params.data.message;
            },
            },
            {
            headerName: 'تاريخ الإرسال',
            field: 'createdAt',
            sortable: true,
            width: 200,
            filter: 'agDateColumnFilter',
            filterParams: {
                buttons: ['apply', 'reset'],
                browserDatePicker: true,
                // `utilities.getFilter` emits one CRUD `filter=` pair per
                // condition, so it cannot express two ANDed/ORed conditions.
                suppressAndOrCondition: true,
                filterOptions: ['equals', 'inRange'],
            },
            valueGetter: function getObject(params) {
                return resolveCreatedAt(params.data);
            },
            },
            {
                headerName: "الإجراءات",
                cellRendererFramework: AgTableBtnCell,
                sortable: false,
                valueGetter: function getObject(params) {
                    return params.data;
                },
                cellRendererParams: {
                    viewRecordDetails: function (thankYouMessage) {
                        self.onViewThankYouMessage(thankYouMessage);
                    },
                    actions: ["view"],
                    permissions: self.permissions,
                },
            },
        ];
        },
        // `groupId` is a plain query param, not the CRUD `filter=` syntax. The API
        // accepts a single group and has no "all" value, so it is omitted when unset.
        async getAllThankYouMessages(query) {
        const groupQuery = this.selectedGroup ? `&groupId=${this.selectedGroup.id}` : "";
        return await thankYouMessagesRepo.fetchThankYouMessages(query + groupQuery);
        },
        async getVolunteerGroups() {
            this.isLoadingGroups = true;
            try {
                const response = await supervisorsRepo.fetchVolunteerGroups();
                this.volunteerGroups = response.data || [];
            } finally {
                this.isLoadingGroups = false;
            }
        },
        onViewThankYouMessage(thankYouMessage) {
            this.thankYouMessage.supervisorName = resolveSupervisorName(thankYouMessage);
            this.thankYouMessage.volunteerName = resolveVolunteerName(thankYouMessage);
            this.thankYouMessage.phoneNumber = resolvePhoneNumber(thankYouMessage);
            this.thankYouMessage.createdAt = resolveCreatedAt(thankYouMessage);
            this.thankYouMessage.message = thankYouMessage.message || "-";
            this.$refs.thankYouMessagePopup.open();
        },
    },
    created() {
        this.gridOptions = {};
        this.gridOptions.columnDefs = this.createColDefs();
        this.getVolunteerGroups();
    },
    mounted() {
        this.gridApi = this.gridOptions.api;
    },
};
</script>

<style>
/* Same theme as the group filter in VolunteersSelector, whose global styles only
   load together with that component. */
.multiselect__option--highlight,
.multiselect__tag-icon:hover,
.multiselect__tag {
    background: rgb(75, 54, 78);
}

.multiselect__tag-icon:after {
    color: white;
}

.multiselect__option--highlight:after {
    background: rgb(75, 54, 78);
}
</style>
